import { css, Global } from '@emotion/react';
import { useMount } from 'ahooks';
import { ConfigProvider } from 'antd';
import useSystem, { SystemSetting } from 'hooks/business/system/useSystem';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { isQuickChat } from 'productLine';
import React, { useContext, useMemo, useCallback, useEffect } from 'react';
import { isMlj } from 'services/chattt';
import createNamedContext from 'utils/createNamedContext';

export type Themes = SystemSetting['theme'];
const defaultTheme: Themes = 'Orange';
const ThemesContext = createNamedContext<{ theme: Themes; onChange: (theme: Themes) => void }>(
  'fett-theme',
  { theme: defaultTheme, onChange: () => {} },
);

export const useThemeContext = () => useContext(ThemesContext);

const commonColors = {
  errorColor: '#FF433D',
  successColor: '#00AA5A',
  infoColor: '#5F8EFC',
};

const mljColors = {
  primaryColor: '#fa6400',
  ...commonColors,
};
export const themesColors = {
  Blue: {
    primaryColor: '#3366FF',
    ...commonColors,
  },
  Orange: {
    primaryColor: '#ee4e2d',
    ...commonColors,
  },
  Purple: {
    primaryColor: '#6954F0',
    ...commonColors,
  },
};
const themesVars: Record<Themes, any> = {
  Blue: css`
    --fett-colors-primary: ${themesColors.Blue.primaryColor}; // app 主色，跟antd组件保持一致
    --fett-colors-primary-focus: #254edb;
    --fett-colors-primary-hover: #6690ff;
    --fett-colors-primary-light: #cbdbfd;
    --fett-colors-second: #fff7f7; // 套餐倒计时弹窗背景色
    --fett-colors-price: #f1694f; // 价格字体色
  `,
  Orange: css`
    --fett-colors-primary: ${themesColors.Orange.primaryColor}; // app 主色，跟antd组件保持一致
    --fett-colors-primary-focus: #cc3020;
    --fett-colors-primary-hover: #f4845f;
    --fett-colors-primary-light: #fed8c8;
    --fett-colors-second: #fff7f7; // 套餐倒计时弹窗背景色
    --fett-colors-price: #f1694f; // 价格字体色
  `,
  Purple: css`
    --fett-colors-primary: ${themesColors.Purple.primaryColor}; // app 主色，跟antd组件保持一致
    --fett-colors-primary-focus: #3129a3;
    --fett-colors-primary-hover: #9581fc;
    --fett-colors-primary-light: #f4f0ff;
    --fett-colors-second: #fff7f7; // 套餐倒计时弹窗背景色
    --fett-colors-price: #f1694f; // 价格字体色
  `,
};
const commonVars = css`
  --fett-app-primary-color: var(--ant-primary-color);
  --fett-app-sider-nav-primary-bg: var(--fett-app-primary-color);
  --fett-free-trail-banner-primay-color: var(--fett-colors-primary);
  --fett-free-trail-banner-bg-color: var(--fett-colors-second);
`;
const diyVars = css`
  --fett-right-height-modal-scroll-body-margin: 4px;
`;
export const ThemesProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    systemSetting: { theme: settingTheme },
    handleThemeSettingChange,
  } = useSystem();
  const { whiteListConfig: { AE_CONVERSATION = false } = {} } = useOrgConfigStateValue();
  const theme = isQuickChat || AE_CONVERSATION ? 'Purple' : settingTheme;
  const updateTheme = useCallback(
    (theme: Themes) => {
      !AE_CONVERSATION && handleThemeSettingChange(theme);
    },
    [handleThemeSettingChange, AE_CONVERSATION],
  );
  const CssVarRoot = useMemo(() => {
    const themeVars = themesVars[theme];
    return isMlj
      ? css`
          :root {
            ${themeVars}
            ${commonVars}
            ${diyVars}
            --fett-app-primary-color: ${mljColors.primaryColor}; // 马六甲专用
          }
        `
      : css`
          :root {
            ${themeVars}
            ${commonVars}
            ${diyVars}
            --fett-app-primary-color: var(--fett-colors-primary);
          }
        `;
  }, [theme]);
  useMount(() => {
    updateTheme(theme);
  });
  useEffect(() => {
    ConfigProvider.config({
      theme: isMlj ? mljColors : themesColors[theme],
    });
  }, [theme]);
  return (
    <ThemesContext.Provider value={{ theme, onChange: updateTheme }}>
      <>
        <Global styles={CssVarRoot} />
        {children}
      </>
    </ThemesContext.Provider>
  );
};
export default ThemesProvider;
